import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack
} from '@mui/material';
import { useSnackbar } from 'notistack';
import companiaService, { Compania } from '../../services/companiaService';

const defaultForm: Compania = {
  idCompania: '',
  nombre: '',
  domicilio: '',
  cp: '',
  localidad: '',
  provincia: '',
  pais: '',
  telefono1: '',
  telefono2: '',
  fax1: '',
  fax2: '',
  mail1: '',
  mail2: '',
  condIva: '',
  cuit: '',
  logo: ''
};

const CompaniaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { idCompania } = useParams();
  const [formData, setFormData] = useState<Compania>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadCompania = useCallback(async () => {
    if (!idCompania || idCompania === 'nuevo') return;
    try {
      setLoading(true);
      const compania = await companiaService.getCompania(idCompania);
      setFormData(compania);
    } catch (error) {
      enqueueSnackbar('Error al cargar la compañía', { variant: 'error' });
      navigate('/sistema/companias');
    } finally {
      setLoading(false);
    }
  }, [idCompania, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (idCompania && idCompania !== 'nuevo') loadCompania();
  }, [idCompania, loadCompania]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.idCompania.trim() || !formData.nombre.trim()) {
        enqueueSnackbar('Complete los campos obligatorios', { variant: 'warning' });
        return;
      }

      if (idCompania && idCompania !== 'nuevo') {
        await companiaService.updateCompania(idCompania, formData);
      } else {
        await companiaService.createCompania(formData);
      }

      enqueueSnackbar('Compañía guardada exitosamente', { variant: 'success' });
      navigate('/sistema/companias');
    } catch (error: any) {
      console.error('Error al guardar compañía:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la compañía', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {idCompania && idCompania !== 'nuevo' ? 'Editar Compañía' : 'Nueva Compañía'}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                  <TextField 
                    name="idCompania" 
                    label="ID Compañía" 
                    value={formData.idCompania} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!(idCompania && idCompania !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField 
                  name="nombre" 
                  label="Nombre" 
                  value={formData.nombre} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="cuit" 
                  label="CUIT" 
                  value={formData.cuit || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="condIva" 
                  label="Condición IVA" 
                  value={formData.condIva || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  name="domicilio" 
                  label="Domicilio" 
                  value={formData.domicilio || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="localidad" 
                  label="Localidad" 
                  value={formData.localidad || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="provincia" 
                  label="Provincia" 
                  value={formData.provincia || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="cp" 
                  label="Código Postal" 
                  value={formData.cp || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="telefono1" 
                  label="Teléfono 1" 
                  value={formData.telefono1 || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="telefono2" 
                  label="Teléfono 2" 
                  value={formData.telefono2 || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="mail1" 
                  label="Email 1" 
                  type="email"
                  value={formData.mail1 || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="mail2" 
                  label="Email 2" 
                  type="email"
                  value={formData.mail2 || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/sistema/companias')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompaniaForm;
